import { payoutAvailabilityMessage } from './payoutAvailability';
import type { PayoutFundingAvailability } from '../types';

// The payment timeline's texts in the viewer's language (ClickUp 869ev0jcn,
// delsteg 5). The API sends each row's texts as keys with values; the
// Swedish strings beside them are only a fallback for a key the web lacks.

export type TimelineText = {
  key: string;
  values?: Record<string, string | number>;
  parts?: TimelineText[];
  separator?: string;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type TimelineDisplayTexts = {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  badgeLabel?: string;
  attemptLabel?: string;
};

type TimelineRow = {
  metadata?: Record<string, unknown>;
  display: TimelineDisplayTexts & {
    texts?: Partial<Record<'title' | 'subtitle' | 'badge' | 'tooltip' | 'attempt', TimelineText>>;
  } & Record<string, unknown>;
};

/** Whole kronor as the rest of the timeline shows them, e.g. "12 500 kr". */
export const formatTimelineKronor = (amountSek: number) =>
  `${new Intl.NumberFormat('sv-SE').format(amountSek)} kr`;

/**
 * One text, or null when this language has no text for its key (or one of
 * its parts). Values ending in `Sek` are whole kronor, `pct` a percentage.
 */
export const timelineTextIn = (
  t: Translate,
  text: TimelineText,
  language: string,
  formatKronor: (amountSek: number) => string = formatTimelineKronor
): string | null => {
  const values: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(text.values ?? {})) {
    values[name] = typeof value === 'number' && name.endsWith('Sek')
      ? formatKronor(value)
      : typeof value === 'number' && name === 'pct'
        ? new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'sv-SE', {
          minimumFractionDigits: value % 1 === 0 ? 0 : 1,
          maximumFractionDigits: 2,
        }).format(value)
        : value;
  }
  if (text.parts) {
    const parts = text.parts.map(part => timelineTextIn(t, part, language, formatKronor));
    if (parts.some(part => part === null)) return null;
    values.parts = parts.join(text.separator ?? ' ');
  }
  const rendered: unknown = t(text.key, { ...values, defaultValue: '' });
  // A key the web lacks comes back empty or as the key itself.
  return typeof rendered === 'string' && rendered !== text.key && (rendered || text.key === 'paymentTimeline.join')
    ? rendered
    : null;
};

/**
 * A timeline row's display with its texts in the viewer's language. Each
 * text falls back to the API's Swedish string when the web has no key for it.
 */
export const paymentTimelineDisplay = <Row extends TimelineRow>(
  row: Row,
  t: Translate,
  language: string,
  formatKronor?: (amountSek: number) => string
): Row['display'] => {
  const texts = row.display.texts;
  if (!texts) return row.display;
  const field = (text: TimelineText | undefined, fallback: string | undefined) =>
    (text ? timelineTextIn(t, text, language, formatKronor) : null) ?? fallback;
  const availability = row.metadata?.payoutAvailability as PayoutFundingAvailability | undefined;
  const waiting = texts.title?.key === 'payoutAvailability.title' && availability?.status === 'awaiting_funds';
  return {
    ...row.display,
    title: field(texts.title, row.display.title),
    subtitle: waiting ? payoutAvailabilityMessage(t, availability, language) : field(texts.subtitle, row.display.subtitle),
    badgeLabel: field(texts.badge, row.display.badgeLabel),
    tooltip: field(texts.tooltip, row.display.tooltip),
    attemptLabel: field(texts.attempt, row.display.attemptLabel),
  };
};
